import {
  Component,
  effect,
  signal,
  computed,
  ViewChild,
  AfterViewInit,
  HostListener,
  ElementRef,
  untracked
} from '@angular/core';
import {
  CompilationsDataService,
  DisplayMapComponent,
  DisplayMapOptions,
  drawPathway,
  TripPOI,
  Pathway,
  GeoCoords,
  adjustMapBBox,
  QuestMetadata,
  Obelisk,
  secondsToTime,
  timeToSeconds,
  QuestViewComponent,
  ObeliskViewComponent,
  QuestsCrudService,
  ExplorationService,
  SvgWrapperComponent
} from 'quest-atlas-angular-components';
import { ActivatedRoute, Router } from '@angular/router';
import { rxResource } from '@angular/core/rxjs-interop';
import { of, forkJoin } from 'rxjs';
import { CommonModule } from '@angular/common';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import * as mapboxgl from 'mapbox-gl';
import { TripShareService } from '../../services/trip-share.service';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'app-trip-view',
  imports: [
    CommonModule,
    TranslocoModule,
    DisplayMapComponent,
    QuestViewComponent,
    ObeliskViewComponent,
    SvgWrapperComponent
  ],
  templateUrl: './trip-view.component.html',
  styleUrl: './trip-view.component.scss'
})
export class TripViewComponent implements AfterViewInit {
  @ViewChild(DisplayMapComponent) displayMap?: DisplayMapComponent;
  @ViewChild('mapSection') mapSection?: ElementRef;

  tripId = signal<number>(null);

  trip = rxResource({
    params: () => ({ id: this.tripId() }),
    stream: ({ params }) => {
      return params.id ? this.compilationsService.getCompilation$(params.id as number) : of(null);
    }
  });


  // Share state
  isSharing = signal<boolean>(false);

  // Loaded full quest and obelisk data
  loadedQuests = signal<QuestMetadata[]>([]);
  loadedObelisks = signal<Obelisk[]>([]);
  isLoadingActivities = signal<boolean>(false);

  // Sidepanel state
  selectedQuest = signal<QuestMetadata | null>(null);
  selectedObelisk = signal<Obelisk | null>(null);
  imageUrlOnPreview = signal<string | null>(null);
  smallWidth = signal<boolean>(false);
  sidepanelOpenOnSmall = signal<boolean>(false);

  @HostListener('window:keyup', ['$event']) onKeyDown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      if (this.imageUrlOnPreview()) {
        this.imageUrlOnPreview.set(null);
        return;
      }

      if (this.selectedQuest()) {
        this.closeQuestSidepanel();
      } else if (this.selectedObelisk()) {
        this.closeObeliskSidepanel();
      }
    }
  }

  @HostListener('window:resize') onResize() {
    this.smallWidth.set(window.innerWidth < 601);
  }

  mapOptions = computed<DisplayMapOptions>(() => {
    // If a quest or obelisk is selected, show only that one
    if (this.selectedQuest()) {
      return {
        quests: [this.selectedQuest()!],
        obelisks: [],
        ignoreFitBounds: true
      };
    }

    if (this.selectedObelisk()) {
      return {
        quests: [],
        obelisks: [this.selectedObelisk()!],
        ignoreFitBounds: true
      };
    }

    // Use loaded full data instead of trip's basic data
    const quests = this.loadedQuests();
    const obelisks = this.loadedObelisks();

    return {
      quests,
      obelisks,
      ignoreFitBounds: true // We handle bbox adjustment manually to include paths and POIs
    };
  });

  private map?: mapboxgl.Map;
  private poiMarkers: mapboxgl.Marker[] = [];

  // Computed values for formatted stats
  formattedDistance = computed(() => {
    const stats = this.getTripStats();
    if (!stats?.totalLength) return null;
    return (stats.totalLength / 1000).toFixed(1);
  });

  formattedTime = computed(() => {
    const stats = this.getTripStats();
    if (!stats?.totalTime) return null;
    const hours = Math.floor(stats.totalTime / 60);
    const minutes = Math.floor(stats.totalTime % 60);
    const hourLabel = this.transloco.translate('hoursShort');
    const minuteLabel = this.transloco.translate('minutesShort');
    if (hours > 0) {
      return `${hours}${hourLabel} ${minutes}${minuteLabel}`;
    }
    return `${minutes}${minuteLabel}`;
  });

  formattedElevationGain = computed(() => {
    const stats = this.getTripStats();
    return stats?.elevationGain ? Math.round(stats.elevationGain) : null;
  });

  formattedElevationLoss = computed(() => {
    const stats = this.getTripStats();
    return stats?.elevationLoss ? Math.round(stats.elevationLoss) : null;
  });

  allActivitiesSectionTitle = computed(() => {
    const hasQuests = this.loadedQuests().length > 0;
    const hasObelisks = this.loadedObelisks().length > 0;

    if (hasQuests && hasObelisks) {
      return this.transloco.translate('allQuestsAndPlaces');
    } else if (hasQuests) {
      return this.transloco.translate('allQuests');
    } else if (hasObelisks) {
      return this.transloco.translate('allPlaces');
    }
    return '';
  });

  private getTripStats() {
    return this.trip.value()?.tripConfig?.stats;
  }

  constructor(
    private compilationsService: CompilationsDataService,
    private questsService: QuestsCrudService,
    private explorationService: ExplorationService,
    private transloco: TranslocoService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private tripShareService: TripShareService,
    private snackBar: MatSnackBar
  ) {
    this.smallWidth.set(window.innerWidth < 601);

    this.activatedRoute.params.subscribe(params => {
      // Handle share token route: /accept-share/compilation/:token
      if (params['token']) {
        this.compilationsService.submitShareToken(params['token']).subscribe({
          next: (data) => {
            this.tripId.set(data.compilationId);
            // Optionally navigate to the direct trip URL
            // this.router.navigate(['/trip', data.compilationId], { replaceUrl: true });
          },
          error: (error) => {
            console.error('Error accepting share token:', error);
            // Show error message or redirect
            this.router.navigate(['/trips']);
          }
        });
      }
      // Handle direct ID routes: /trip/:id or /show-compilation/:id
      else if (params['id']) {
        const id = params['id'] === 'new' ? null : Number(params['id']);
        this.tripId.set(Number(id));
      }
    });

    // Load full quest and obelisk data when trip is loaded
    effect(() => {
      const trip = this.trip.value();
      if (trip && !untracked(this.isLoadingActivities)) {
        this.loadActivitiesData(trip);
      }
    });

    effect(() => {
      console.log('Trip data:', this.trip.value());
      const trip = this.trip.value();
      if (trip && this.map) {
        this.drawPathsAndPOIs();
      }
    });
  }

  private loadActivitiesData(trip: any): void {
    this.isLoadingActivities.set(true);

    const questIds = trip.quests?.map((q: any) => q.id).filter((id: any) => id) || [];
    const obeliskUuids = trip.obelisks?.map((o: any) => o.uuid).filter((uuid: any) => uuid) || [];

    const questRequests = questIds.map((id: number) => this.questsService.loadQuestById(id));
    const obeliskRequests = obeliskUuids.map((uuid: string) => this.explorationService.getObeliskById$(uuid as any));

    // Load all quests and obelisks in parallel
    forkJoin({
      quests: questRequests.length > 0 ? forkJoin(questRequests) : of([]),
      obelisks: obeliskRequests.length > 0 ? forkJoin(obeliskRequests) : of([])
    }).subscribe({
      next: (result) => {
        this.loadedQuests.set(result.quests as QuestMetadata[]);
        this.loadedObelisks.set(result.obelisks as Obelisk[]);
        this.isLoadingActivities.set(false);
      },
      error: (error) => {
        console.error('Error loading activities:', error);
        this.isLoadingActivities.set(false);
        // Fallback to basic trip data if loading fails
        this.loadedQuests.set(trip.quests || []);
        this.loadedObelisks.set(trip.obelisks || []);
      }
    });
  }

  ngAfterViewInit(): void {
    // Map initialization will be handled by the effect when data is loaded
  }

  onMapInit(map: mapboxgl.Map): void {
    this.map = map;

    // Disable scroll zoom to prevent zooming when scrolling the page
    this.map.scrollZoom.disable();

    // Wait for map to be fully loaded before drawing
    this.map.on('load', () => {
      const trip = this.trip.value();
      if (trip) {
        this.drawPathsAndPOIs();
      }
    });

    // If map is already loaded, draw immediately
    if (this.map.loaded()) {
      const trip = this.trip.value();
      if (trip) {
        this.drawPathsAndPOIs();
      }
    }
  }

  onQuestClick(quest: QuestMetadata): void {
    // Format approx time if available
    if (quest.info?.approxTime) {
      quest.info.approxTime = secondsToTime(timeToSeconds(quest.info.approxTime), true);
    }

    this.selectQuest(quest);
    this.flyToLocation(quest.locationPoint);
  }

  onObeliskClick(obelisk: Obelisk): void {
    this.selectObelisk(obelisk);
    this.flyToLocation(obelisk.location);
  }

  onQuestCardClick(quest: QuestMetadata): void {
    this.scrollToMap();
    this.selectQuest(quest);
    this.flyToLocation(quest.locationPoint);
  }

  onObeliskCardClick(obelisk: Obelisk): void {
    this.scrollToMap();
    this.selectObelisk(obelisk);
    this.flyToLocation(obelisk.location);
  }

  onClusterClick(cluster: any): void {
    if (this.isQuest(cluster)) {
      const fullQuest = this.loadedQuests().find(q => q.id === cluster.id);
      if (fullQuest) {
        this.onQuestClick(fullQuest);
      }
    } else if (this.isObelisk(cluster)) {
      const fullObelisk = this.loadedObelisks().find(o => o.uuid === cluster.uuid);
      if (fullObelisk) {
        this.onObeliskCardClick(fullObelisk);
      }
    }
  }

  private selectQuest(quest: QuestMetadata): void {
    this.selectedQuest.set(quest);
    this.selectedObelisk.set(null);

    if (this.smallWidth()) {
      this.sidepanelOpenOnSmall.set(false);
    }
  }

  private selectObelisk(obelisk: Obelisk): void {
    this.selectedObelisk.set(obelisk);
    this.selectedQuest.set(null);

    if (this.smallWidth()) {
      this.sidepanelOpenOnSmall.set(false);
    }
  }

  private flyToLocation(location?: GeoCoords): void {
    if (this.map && location) {
      this.map.flyTo({
        center: [location.lng, location.lat],
        zoom: 15
      });
    }
  }

  closeQuestSidepanel(): void {
    this.selectedQuest.set(null);
    // Redraw all paths and POIs
    this.drawPathsAndPOIs();
  }

  closeObeliskSidepanel(): void {
    this.selectedObelisk.set(null);
    // Redraw all paths and POIs
    this.drawPathsAndPOIs();
  }

  closeSidepanel(): void {
    if (this.selectedObelisk()) {
      this.closeObeliskSidepanel();
    } else if (this.selectedQuest()) {
      this.closeQuestSidepanel();
    }
  }

  private scrollToMap(): void {
    if (this.mapSection) {
      this.mapSection.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  private drawPathsAndPOIs(): void {
    if (!this.map || !this.map.loaded()) return;

    const trip = this.trip.value();
    if (!trip?.tripConfig) return;

    // Clear existing POI markers
    this.poiMarkers.forEach(marker => marker.remove());
    this.poiMarkers = [];

    const allCoords: GeoCoords[] = [];

    // Collect coordinates from loaded quests and obelisks
    this.collectClusterCoordinates(allCoords);

    // Draw and collect coordinates from paths
    this.processPathways(trip.tripConfig.paths, allCoords);

    if (trip.tripConfig.locations) {
      trip.tripConfig.locations.forEach(location => {
        this.processPathways(location.paths, allCoords);
        this.processPOIs(location.poi, allCoords);
      });
    }

    // Draw and collect coordinates from POIs
    this.processPOIs(trip.tripConfig.poi, allCoords);

    // Adjust map bounds to show all content
    this.adjustMapBounds(allCoords);
  }

  private collectClusterCoordinates(allCoords: GeoCoords[]): void {
    this.loadedQuests().forEach(quest => {
      if (quest.locationPoint) {
        allCoords.push({ lat: quest.locationPoint.lat, lng: quest.locationPoint.lng });
      }
    });

    this.loadedObelisks().forEach(obelisk => {
      if (obelisk.location) {
        allCoords.push(obelisk.location);
      }
    });
  }

  private processPathways(pathways: Pathway[] | undefined, allCoords: GeoCoords[]): void {
    if (!pathways) return;

    pathways.forEach(pathway => {
      this.drawPathwaySafe(pathway);
      this.collectPathwayCoordinates(pathway, allCoords);
    });
  }

  private collectPathwayCoordinates(pathway: Pathway, allCoords: GeoCoords[]): void {
    // Collect from vertices array
    if (pathway.vertices && pathway.vertices.length > 0) {
      allCoords.push(...pathway.vertices);
    }

    // Collect from path.vertices (QGraph)
    if (pathway.path?.vertices) {
      Object.values(pathway.path.vertices).forEach(vertex => {
        allCoords.push(vertex.coords);
      });
    }
  }

  private processPOIs(pois: TripPOI[] | undefined, allCoords: GeoCoords[]): void {
    if (!pois) return;

    this.drawPOIs(pois);
    pois.forEach(poi => allCoords.push(poi.coords));
  }

  private adjustMapBounds(allCoords: GeoCoords[]): void {
    if (allCoords.length === 0) return;

    if (allCoords.length > 1) {
      const lngs = allCoords.map(c => c.lng);
      const lats = allCoords.map(c => c.lat);
      const sw: GeoCoords = { lng: Math.min(...lngs), lat: Math.min(...lats) };
      const ne: GeoCoords = { lng: Math.max(...lngs), lat: Math.max(...lats) };
      adjustMapBBox([ne, sw], this.map!, 50);
    } else {
      this.map!.flyTo({
        center: [allCoords[0].lng, allCoords[0].lat],
        zoom: 14
      });
    }
  }

  private drawPathwaySafe(pathway: Pathway): void {
    if (!this.map || !pathway.path) return;

    try {
      // Check if source already exists
      const sourceId = 'pathway-' + pathway.uuid;
      if (this.map.getSource(sourceId)) {
        // Source exists, just update it
        return;
      }

      drawPathway(pathway, this.map);
    } catch (error) {
      console.error('Error drawing pathway:', error, pathway);
    }
  }

  private drawPOIs(pois: TripPOI[]): void {
    if (!this.map || !pois) return;

    pois.forEach(poi => {
      // Create custom marker element with maki icon (same as trip-editor)
      const el = document.createElement('div');
      el.className = 'poi-marker';
      el.style.width = '30px';
      el.style.height = '30px';
      el.style.backgroundImage = `url('/assets/maki/${poi.icon}.svg')`;
      el.style.backgroundSize = '18px 18px';
      el.style.backgroundPosition = 'center';
      el.style.backgroundRepeat = 'no-repeat';
      el.style.cursor = 'pointer';

      // Create popup
      const popup = new mapboxgl.Popup({ offset: 25 }).setHTML(`
        <div style="padding: 8px;">
          <h3 style="margin: 0 0 4px 0; font-size: 14px; font-weight: 600;">${poi.name}</h3>
          ${poi.description ? `<p style="margin: 4px 0; font-size: 12px;">${poi.description}</p>` : ''}
          ${poi.website ? `<a href="${poi.website}" target="_blank" style="font-size: 12px;">Visit website</a>` : ''}
        </div>
      `);

      // Create marker
      const marker = new mapboxgl.Marker(el)
        .setLngLat([poi.coords.lng, poi.coords.lat])
        .setPopup(popup)
        .addTo(this.map!);

      this.poiMarkers.push(marker);
    });
  }

  getSectionImages(section: any): { url: string; caption: string }[] {
    return section.images || [];
  }

  obeliskImageUri(obelisk: any): string {
    if (!obelisk.images?.[0]?.uri) {
      return '';
    }

    if (obelisk.createdAt < new Date('2024-08-31')) {
      return obelisk.images[0].uri;
    }

    const split = obelisk.images[0].uri.split('/');
    split[split.length - 1] = 'thumbnails/' + split[split.length - 1];
    return split.join('/');
  }

  isQuest(cluster: any): boolean {
    return cluster && 'steps' in cluster;
  }

  isObelisk(cluster: any): boolean {
    return cluster && 'uuid' in cluster && !('steps' in cluster);
  }

  getClusterImage(cluster: any): string {
    if (this.isQuest(cluster)) {
      // Quest - get from info.images
      if (cluster.info?.images?.length > 0) {
        return cluster.info.images[0].url;
      }
      return ''; // Will use fallback in template
    } else if (this.isObelisk(cluster)) {
      // Obelisk - use obeliskImageUri
      return this.obeliskImageUri(cluster);
    }
    return '';
  }

  hasClusterImage(cluster: any): boolean {
    return this.getClusterImage(cluster) !== '';
  }

  shareTrip(): void {
    const trip = this.trip.value();
    if (!trip?.id) return;

    this.isSharing.set(true);

    // Try to create a share token first (for owned trips)
    // If it fails, fall back to direct link (for public trips)
    this.compilationsService.createShareToken(trip.id).subscribe({
      next: (tokenResponse) => {
        // Successfully created token - this is an owned trip
        const shareUrl = `${window.location.origin}/accept-share/compilation/${tokenResponse.token}`;
        this.copyAndShowSuccess(shareUrl);
      },
      error: (_error) => {
        // Failed to create token - likely a public trip we're viewing
        // Use direct link instead
        const shareUrl = `${window.location.origin}/show-compilation/${trip.id}`;
        this.copyAndShowSuccess(shareUrl);
      }
    });
  }

  private copyAndShowSuccess(shareUrl: string): void {
    this.tripShareService.copyToClipboard(shareUrl).then(() => {
      this.isSharing.set(false);
      this.snackBar.open(
        this.transloco.translate('linkCopiedToClipboard'),
        this.transloco.translate('close'),
        {
          duration: 3000,
          verticalPosition: 'bottom',
          horizontalPosition: 'center'
        }
      );
    }).catch((error) => {
      console.error('Error copying to clipboard:', error);
      this.isSharing.set(false);
    });
  }
}
